/**
 * The root route: it renders nothing of its own, and it is where the session is
 * resolved before any guard runs.
 *
 * `beforeLoad` here is the only place the session is loaded. It goes through the
 * query cache, so the answer is the same object the React tree is reading from
 * `useQuery` (`src/auth/session.ts` says why that matters), and every child
 * route's guard gets it on `context.session` without a second request — one read
 * per thirty seconds of navigation rather than one per route.
 *
 * The providers are **not** here. They are in `main.tsx`, above `RouterProvider`,
 * so that the 404 and the error boundary — which replace this route's component
 * rather than render inside it — still have a principal and a query client.
 */

import { createRootRouteWithContext, Outlet } from '@tanstack/react-router'

import { NotFound } from '../errors/NotFound.js'
import { RouteError } from '../errors/RouteError.js'
import { sessionQueryOptions } from '../auth/session.js'
import type { RouterContext } from '../router-context.js'

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.query(sessionQueryOptions(context.auth))
    return { session }
  },
  component: Outlet,
  notFoundComponent: NotFound,
  errorComponent: RouteError,
})
